import { ActionCreator } from 'redux';
import { Segment, SegmentPayload } from '@dev/ee-api/ee-api-models';
import { cloneDeep, map } from 'lodash-es';
import { store } from '@shared-store/shared-store';

import { ApplicationState } from '@/store';

import {
    RuleEvaluationResultInterface,
    SegmentAddErrorAction,
    SegmentAddStartAction,
    SegmentAddSuccessAction,
    SegmentClearRuleEvaluationResultAction,
    SegmentDeleteErrorAction,
    SegmentDeleteStartAction,
    SegmentDeselectAction,
    SegmentLoadByContactErrorAction,
    SegmentLoadByContactStartAction,
    SegmentLoadByContactSuccessAction,
    SegmentLoadFetchErrorAction,
    SegmentLoadFetchStartAction,
    SegmentLoadFetchSuccessAction,
    SegmentRuleEvaluateAction,
    SegmentSelectAction,
    SegmentsGetNamesErrorAction,
    SegmentsGetNamesStartAction,
    SegmentsGetNamesSuccessAction,
    SegmentsLoadHistoryTrackedError,
    SegmentsLoadHistoryTrackedStart,
    SegmentsLoadHistoryTrackedSuccess,
    SegmentsState,
    SegmentUpdateEditedAction,
    SegmentUpdateEditedSuccessAction,
    SegmentUpdateRuleEvaluationResultAction,
    SetCollectionAction,
    SetPaginationAction,
    SingleSegmentLoadFetchErrorAction,
    SingleSegmentLoadFetchStartAction,
    SingleSegmentLoadFetchSuccessAction,
} from './types';

export const SingleSegmentLoadStart: ActionCreator<SingleSegmentLoadFetchStartAction> = (
    name: string
): SingleSegmentLoadFetchStartAction => ({
    type: '@@segments/SINGLE_FETCH_START',
    payload: {
        name,
        isLoadingSegments: true,
    },
});

export const SingleSegmentLoadError: ActionCreator<
    SingleSegmentLoadFetchErrorAction
> = (): SingleSegmentLoadFetchErrorAction => ({
    type: '@@segments/SINGLE_FETCH_ERROR',
    payload: {
        isLoadingSegments: false,
    },
});

export const SingleSegmentLoadSuccess: ActionCreator<SingleSegmentLoadFetchSuccessAction> = (
    selected: Segment
): SingleSegmentLoadFetchSuccessAction => ({
    type: '@@segments/SINGLE_FETCH_SUCCESS',
    payload: {
        selected,
        isLoadingSegments: false,
    },
});

export const SegmentsLoadStart: ActionCreator<SegmentLoadFetchStartAction> = (params?: {
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
    includeHistory?: boolean;
    searchName?: string;
}): SegmentLoadFetchStartAction => ({
    type: '@@segments/FETCH_START',
    payload: {
        searchName: params?.searchName,
        from: params?.from,
        to: params?.to,
        limit: params?.limit,
        offset: params?.offset,
        includeHistory: params?.includeHistory || false,
        isLoadingSegments: true,
    },
});

export const SegmentsLoadError: ActionCreator<SegmentLoadFetchErrorAction> = (): SegmentLoadFetchErrorAction => ({
    type: '@@segments/FETCH_ERROR',
    payload: {
        isLoadingSegments: false,
    },
});

export const SegmentsLoadSuccess: ActionCreator<SegmentLoadFetchSuccessAction> = (
    collection: Segment[],
    currentCollectionTotalCount: number
): SegmentLoadFetchSuccessAction => ({
    type: '@@segments/FETCH_SUCCESS',
    payload: {
        collection,
        currentCollectionTotalCount,
        isLoadingSegments: false,
    },
});

export const SegmentsGetNamesStart: ActionCreator<SegmentsGetNamesStartAction> = (): SegmentsGetNamesStartAction => ({
    type: '@@segments/FETCH_SEGMENTNAMES_START',
});

export const SegmentsGetNamesError: ActionCreator<SegmentsGetNamesErrorAction> = (): SegmentsGetNamesErrorAction => ({
    type: '@@segments/FETCH_SEGMENTNAMES_ERROR',
});

export const SegmentsGetNamesSuccess: ActionCreator<SegmentsGetNamesSuccessAction> = (
    segmentNamesCollection: Segment[]
): SegmentsGetNamesSuccessAction => ({
    type: '@@segments/FETCH_SEGMENTNAMES_SUCCESS',
    payload: {
        segmentNamesCollection,
    },
});

export const SegmentDeleteStart: ActionCreator<SegmentDeleteStartAction> = (
    name: string,
    perPage?: number,
    activePage?: number,
    searchName?: string
): SegmentDeleteStartAction => ({
    type: '@@segments/DELETE_START',
    payload: {
        name,
        perPage,
        activePage,
        searchName,
    },
});

export const SegmentDeleteError: ActionCreator<SegmentDeleteErrorAction> = (): SegmentDeleteErrorAction => ({
    type: '@@segments/DELETE_ERROR',
});

export const SegmentAddStart: ActionCreator<SegmentAddStartAction> = (
    name: string,
    rule: string,
    trackHistory = false
): SegmentAddStartAction => ({
    type: '@@segments/ADD_START',
    payload: {
        name,
        rule,
        trackHistory,
    },
    meta: {
        thunk: true,
    },
});

export const SegmentAddError: ActionCreator<SegmentAddErrorAction> = (
    meta: Record<string, any>
): SegmentAddErrorAction => ({
    type: '@@segments/ADD_ERROR',
    meta,
    error: true,
});

export const SegmentAddSuccess: ActionCreator<SegmentAddSuccessAction> = (
    collection: Segment[],
    meta: Record<string, any>
): SegmentAddSuccessAction => ({
    type: '@@segments/ADD_SUCCESS',
    payload: {
        collection,
    },
    meta,
});

export const SegmentSelect: ActionCreator<SegmentSelectAction> = (selected: Segment): SegmentSelectAction => ({
    type: '@@segments/SELECT',
    payload: {
        selected,
    },
});

export const SegmentDeselect: ActionCreator<SegmentDeselectAction> = (): SegmentDeselectAction => ({
    type: '@@segments/DESELECT',
    payload: {
        selected: null,
    },
});

export const SetSegmentCollection: ActionCreator<SetCollectionAction> = (
    visibleSet: Segment[]
): SetCollectionAction => ({
    type: '@@segments/SET_COLLECTION',
    payload: {
        visibleSet,
    },
});

export const SegmentsLoadHistoryTrackedStartAction: ActionCreator<SegmentsLoadHistoryTrackedStart> = (
    includeHistory?: boolean,
    from?: string,
    to?: string
): SegmentsLoadHistoryTrackedStart => ({
    type: '@@segments/LOAD_HISTORY_TRACKED_START',
    payload: {
        isLoadingSegments: true,
        includeHistory,
        from,
        to,
    },
});

export const SegmentsLoadHistoryTrackedErrorAction: ActionCreator<
    SegmentsLoadHistoryTrackedError
> = (): SegmentsLoadHistoryTrackedError => ({
    type: '@@segments/LOAD_HISTORY_TRACKED_ERROR',
    payload: {
        isLoadingSegments: false,
    },
});

export const SegmentsLoadHistoryTrackedSuccessAction: ActionCreator<SegmentsLoadHistoryTrackedSuccess> = (
    historyTrackedCollection: Segment[]
): SegmentsLoadHistoryTrackedSuccess => ({
    type: '@@segments/LOAD_HISTORY_TRACKED_SUCCESS',
    payload: {
        isLoadingSegments: false,
        historyTrackedCollection,
    },
});

export const SegmentLoadByContactStart = (email: string, showErrorToast = false): SegmentLoadByContactStartAction => ({
    type: '@@segments/LOAD_BY_CONTACT_START',
    payload: {
        email,
        showErrorToast,
    },
});

export const SegmentLoadByContactError: ActionCreator<
    SegmentLoadByContactErrorAction
> = (): SegmentLoadByContactErrorAction => ({
    type: '@@segments/LOAD_BY_CONTACT_ERROR',
});

export const SegmentLoadByContactSuccess: ActionCreator<SegmentLoadByContactSuccessAction> = (
    collection: Segment[]
): SegmentLoadByContactSuccessAction => ({
    type: '@@segments/LOAD_BY_CONTACT_SUCCESS',
    payload: {
        collection,
    },
});

export const SegmentRuleEvaluate: ActionCreator<SegmentRuleEvaluateAction> = (
    rule: string
): SegmentRuleEvaluateAction => ({
    type: '@@segments/EVALUATE_RULE',
    payload: {
        rule,
    },
});

export const SegmentUpdateRuleEvaluationResult: ActionCreator<SegmentUpdateRuleEvaluationResultAction> = (
    ruleEvaluationResult: RuleEvaluationResultInterface
): SegmentUpdateRuleEvaluationResultAction => ({
    type: '@@segments/UPDATE_RULE_EVALUATION_RESULT',
    payload: {
        ruleEvaluationResult,
    },
});

export const SegmentClearRuleEvaluationResult: ActionCreator<
    SegmentClearRuleEvaluationResultAction
> = (): SegmentClearRuleEvaluationResultAction => ({
    type: '@@segments/CLEAR_RULE_EVALUATION_RESULT',
    payload: {
        ruleEvaluationResult: null,
    },
});

export const SegmentUpdateEdited: ActionCreator<SegmentUpdateEditedAction> = (
    name: string,
    segmentPayload: SegmentPayload
): SegmentUpdateEditedAction => ({
    type: '@@segments/UPDATE_EDITED',
    payload: {
        name,
        segmentPayload,
    },
});

export const SegmentUpdateEditedSuccess: ActionCreator<SegmentUpdateEditedSuccessAction> = (
    name: string,
    segmentPayload: SegmentPayload
): SegmentUpdateEditedSuccessAction => {
    const collection = map(cloneDeep(store.getState().segments.collection), (segment: Segment): Segment => {
        if (segment.Name === name) {
            segment = { ...segment, ...segmentPayload };
        }
        return segment;
    });

    return {
        type: '@@segments/UPDATE_EDITED_SUCCESS',
        payload: {
            collection,
        },
    };
};

export const SetPagination: ActionCreator<SetPaginationAction> = (params: {
    activePage?: number;
    perPage?: number;
    searchName?: string;
}): SetPaginationAction => {
    const { activePage, perPage, searchName } = params;
    return {
        type: '@segments/SET_PAGINATION',
        payload: {
            activePage,
            perPage,
            searchName,
        },
    };
};

export const getSegmentsCount = (state: ApplicationState): number => state.segments.currentCollectionTotalCount;
export const getSegmentsStore = (store: ApplicationState): SegmentsState => store.segments;
export const getSelectedSegment = (store: ApplicationState) => store.segments?.selected;
